import type { FilterConfig, FilterState } from '@/components/filter-bar';
import { EVENT_TYPE_LABELS } from '@/features/abuse/abuse-meta';
import { ABUSE_SUBJECT_SORTS, ABUSE_SUBJECT_TYPES, type AbuseEventsRequest, type AbuseSubjectSort, type AbuseSubjectType, type AbuseTimeRange } from '@/services/api/abuse';
import { MS_PER_DAY, toUtcDateKey, utcEndOfDayIso, utcStartOfDayIso } from '@/utils/date';

/** Event retention: the API rejects windows longer than this */
export const MAX_RANGE_DAYS = 90;

export type AbuseRangePreset = '7d' | '30d' | '90d' | 'custom';

export const RANGE_PRESET_OPTIONS: Array<{ value: Exclude<AbuseRangePreset, 'custom'>; label: string }> = [
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
];

export const DEFAULT_RANGE_PRESET: AbuseRangePreset = '30d';

const PRESET_DAYS: Record<Exclude<AbuseRangePreset, 'custom'>, number> = { '7d': 7, '30d': 30, '90d': 90 };
const CUSTOM_FALLBACK_DAYS = 30;

/**
 * Whole UTC days ending with today, so revisiting the page the same day yields the same query keys
 * (and cache) instead of a window that moves with every millisecond. Also aligns with the daily buckets.
 */
export const presetToRange = (preset: Exclude<AbuseRangePreset, 'custom'>, now: Date = new Date()): AbuseTimeRange => ({
  from: utcStartOfDayIso(toUtcDateKey(new Date(now.getTime() - (PRESET_DAYS[preset] - 1) * MS_PER_DAY))),
  to: utcEndOfDayIso(toUtcDateKey(now)),
});

/** Date-input bounds to a UTC window; a missing end means the end of today, a missing start the default span before the end */
export const customToRange = ([fromDate, toDate]: string[], now: Date = new Date()): AbuseTimeRange => {
  const to = utcEndOfDayIso(toDate || toUtcDateKey(now));
  const from = fromDate ? utcStartOfDayIso(fromDate) : new Date(Date.parse(to) - CUSTOM_FALLBACK_DAYS * MS_PER_DAY).toISOString();

  return { from, to };
};

/** Why the API would reject a window, worded for the operator, or null when it is valid */
export const rangeProblem = ({ from, to }: AbuseTimeRange): string | null => {
  const spanMs = Date.parse(to) - Date.parse(from);

  if (spanMs <= 0) return 'The start of the range must be before its end.';
  if (spanMs > MAX_RANGE_DAYS * MS_PER_DAY) return `Choose a window of ${MAX_RANGE_DAYS} days or less: older events have expired.`;

  return null;
};

const EVENT_TYPE_BY_LABEL: Record<string, string> = Object.fromEntries(Object.entries(EVENT_TYPE_LABELS).map(([code, label]) => [label, code]));

/** Services come from the summary's breakdown, so only services seen in the window are offered */
export const eventFilterConfigs = (services: string[]): Array<FilterConfig<unknown>> => [
  { getValue: () => undefined, key: 'eventType', options: Object.values(EVENT_TYPE_LABELS), title: 'Event type', type: 'select' },
  { getValue: () => undefined, key: 'service', options: services, title: 'Service', type: 'select' },
];

export const filtersToEventRequest = (state: FilterState): Pick<AbuseEventsRequest, 'eventType' | 'service'> => {
  const eventTypeLabel = state.eventType?.[0];
  const service = state.service?.[0];

  return {
    ...(eventTypeLabel ? { eventType: EVENT_TYPE_BY_LABEL[eventTypeLabel] ?? eventTypeLabel } : {}),
    ...(service ? { service } : {}),
  };
};

export interface AbuseDashboardState {
  preset: AbuseRangePreset;
  /** Date-input values, YYYY-MM-DD; only read when the preset is custom */
  customDates: string[];
  subjectsType: AbuseSubjectType;
  subjectsSort: AbuseSubjectSort;
  feedType: AbuseSubjectType;
  eventFilters: FilterState;
}

const isSubjectType = (value: string | null): value is AbuseSubjectType => (ABUSE_SUBJECT_TYPES as readonly (string | null)[]).includes(value);
const isSubjectSort = (value: string | null): value is AbuseSubjectSort => (ABUSE_SUBJECT_SORTS as readonly (string | null)[]).includes(value);
const isRangePreset = (value: string | null): value is AbuseRangePreset => value === 'custom' || RANGE_PRESET_OPTIONS.some((option) => option.value === value);

/** Dashboard selections live in the URL so Back from a subject restores them; unknown values fall back to the defaults */
export const readDashboardState = (params: URLSearchParams): AbuseDashboardState => {
  const range = params.get('range');
  const subjects = params.get('subjects');
  const sort = params.get('sort');
  const feed = params.get('feed');
  const eventType = params.get('eventType');
  const service = params.get('service');

  return {
    preset: isRangePreset(range) ? range : DEFAULT_RANGE_PRESET,
    customDates: [params.get('from') ?? '', params.get('to') ?? ''],
    subjectsType: isSubjectType(subjects) ? subjects : 'user',
    subjectsSort: isSubjectSort(sort) ? sort : 'peak',
    feedType: isSubjectType(feed) ? feed : 'user',
    eventFilters: {
      ...(eventType ? { eventType: [EVENT_TYPE_LABELS[eventType] ?? eventType] } : {}),
      ...(service ? { service: [service] } : {}),
    },
  };
};

/** Defaults are left out so the bare page URL stays clean */
export const toDashboardParams = (state: AbuseDashboardState): URLSearchParams => {
  const { eventType, service } = filtersToEventRequest(state.eventFilters);
  const [fromDate, toDate] = state.preset === 'custom' ? state.customDates : [];
  const entries: Array<[string, string | undefined]> = [
    ['range', state.preset === DEFAULT_RANGE_PRESET ? undefined : state.preset],
    ['from', fromDate || undefined],
    ['to', toDate || undefined],
    ['subjects', state.subjectsType === 'user' ? undefined : state.subjectsType],
    ['sort', state.subjectsSort === 'peak' ? undefined : state.subjectsSort],
    ['feed', state.feedType === 'user' ? undefined : state.feedType],
    ['eventType', eventType],
    ['service', service],
  ];

  return new URLSearchParams(entries.filter((entry): entry is [string, string] => entry[1] !== undefined));
};
